// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient `window.gtag` types; needed here because consumer tsconfigs (storefront/console) don't include packages/shared/types/
/// <reference path="../types/gtag.d.ts" />
/**
 * SF-INFRA-003 — Google Analytics 4 helper module.
 *
 * Despite the `use*` prefix (kept for historical familiarity with the SPA's
 * other hook-shaped modules), this file intentionally exports a set of
 * imperative helpers, NOT a React hook. Consumers can safely call these from
 * effects, event handlers, thunks, or TanStack Query mutation callbacks —
 * there is no React-lifecycle coupling.
 *
 * Guard strategy
 * --------------
 * Every helper short-circuits in two situations:
 *
 *   1. `import.meta.env.DEV === true` — local dev should never emit live GA4
 *      events. Vite exposes `DEV` via its static env.
 *   2. `window.gtag` is not a function — either the script never loaded
 *      (no `VITE_GA4_MEASUREMENT_ID` at build time) or we're running in a
 *      non-browser context (Vitest jsdom, SSR). Calls no-op silently so
 *      analytics failures never crash a user flow.
 *
 * E-commerce event shape
 * ----------------------
 * Helpers emit GA4 Enhanced E-commerce events with an `items[]` array:
 * `{ item_id, item_name, price, quantity, item_category?, item_brand? }`,
 * mapped from a narrow local interface. Once the real product domain lands
 * (PL-PROD-001) we'll swap `AnalyticsProductLike` for the canonical
 * `ProductDTO` — every caller already conforms to this shape.
 */

/**
 * Narrow product shape the analytics helpers actually need. Defined locally
 * so this module doesn't take a hard dependency on a DTO type that hasn't
 * landed yet.
 *
 * TODO(PL-PROD-001): replace with the shared `ProductDTO` type once the
 * product domain lands. Keeping this permissive enough that a real DTO will
 * satisfy it (string | number id, string name, numeric price, optional
 * category/brand).
 */
export interface AnalyticsProductLike {
  id: string | number;
  name: string;
  price: number;
  category?: string;
  brand?: string;
}

export interface AnalyticsCartItem {
  product: AnalyticsProductLike;
  quantity: number;
}

export interface AnalyticsBeginCheckoutCart {
  items: AnalyticsCartItem[];
  value?: number;
}

/**
 * True when the helper should stay silent for this call. Inlined deliberately:
 * every helper MUST run this check before touching `window`, so keeping the
 * two-line check inline (instead of `return guard() && ...`) makes the guard
 * behaviour obvious at the call site and avoids an extra function frame in
 * production traces.
 */
function shouldSkip(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return true;
  if (typeof window.gtag !== 'function') return true;
  return false;
}

/**
 * Map a cart entry into GA4's expected e-commerce `items[]` shape. Keeps the
 * public helpers tight.
 */
function toItemsArray(items: AnalyticsCartItem[]): Record<string, unknown>[] {
  return items.map(({ product, quantity }) => toItem(product, quantity));
}

function toItem(
  product: AnalyticsProductLike,
  quantity: number,
): Record<string, unknown> {
  const item: Record<string, unknown> = {
    item_id: String(product.id),
    item_name: product.name,
    price: product.price,
    quantity,
  };
  if (product.category !== undefined) item.item_category = product.category;
  if (product.brand !== undefined) item.item_brand = product.brand;
  return item;
}

/**
 * Fire a GA4 `page_view` event. Called by the `<Analytics>` component on
 * every react-router location change (and on initial mount).
 */
export function trackPageView(path: string, title?: string): void {
  if (shouldSkip()) return;
  const params: Record<string, unknown> = { page_path: path };
  if (title !== undefined) params.page_title = title;
  window.gtag!('event', 'page_view', params);
}

/**
 * Generic event emitter. Prefer the typed `track*` helpers below for the
 * GA4 Enhanced E-commerce events — this escape hatch exists for custom
 * events not covered by the standard schema (feature interactions, etc.).
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (shouldSkip()) return;
  window.gtag!('event', eventName, params);
}

export function trackViewItem(product: AnalyticsProductLike): void {
  if (shouldSkip()) return;
  window.gtag!('event', 'view_item', {
    currency: 'USD',
    value: product.price,
    items: [toItem(product, 1)],
  });
}

export function trackAddToCart(
  product: AnalyticsProductLike,
  quantity: number,
): void {
  if (shouldSkip()) return;
  window.gtag!('event', 'add_to_cart', {
    currency: 'USD',
    value: product.price * quantity,
    items: [toItem(product, quantity)],
  });
}

export function trackRemoveFromCart(product: AnalyticsProductLike): void {
  if (shouldSkip()) return;
  window.gtag!('event', 'remove_from_cart', {
    currency: 'USD',
    value: product.price,
    items: [toItem(product, 1)],
  });
}

export function trackBeginCheckout(cart: AnalyticsBeginCheckoutCart): void {
  if (shouldSkip()) return;
  const value =
    cart.value ??
    cart.items.reduce(
      (sum, { product, quantity }) => sum + product.price * quantity,
      0,
    );
  window.gtag!('event', 'begin_checkout', {
    currency: 'USD',
    value,
    items: toItemsArray(cart.items),
  });
}

export function trackPurchase(
  orderId: string,
  revenue: number,
  items: AnalyticsCartItem[],
): void {
  if (shouldSkip()) return;
  window.gtag!('event', 'purchase', {
    transaction_id: orderId,
    value: revenue,
    currency: 'USD',
    items: toItemsArray(items),
  });
}
